import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { AlertCircle, Search, X, MapPin, ChevronRight } from 'lucide-react'
import api from '../../services/api'

const METHODOLOGIES = [
  {
    value: 'icam',
    label: 'ICAM',
    full: 'Incident Cause Analysis Method',
    description: 'Australian-standard methodology. Analyses immediate causes, contributing factors, and absent/failed defences. Best for serious and notifiable incidents.',
    badge: 'Recommended',
    badgeColor: '#0A84FF',
    uses: ['Notifiable incidents', 'Serious injuries', 'High-potential near misses'],
  },
  {
    value: '5_why',
    label: '5 Why',
    full: 'Five Whys Root Cause',
    description: 'Iteratively asks "why?" to drill to the root cause. Fast and effective for simpler incidents with a clear causal chain.',
    badge: 'Simple',
    badgeColor: '#00D4AA',
    uses: ['Low-severity incidents', 'Process failures', 'Near misses'],
  },
  {
    value: 'fishbone',
    label: 'Fishbone',
    full: 'Fishbone / Ishikawa Diagram',
    description: 'Maps causes across 6 categories (Man, Machine, Method, Material, Measurement, Environment). Good for complex problems with many contributing factors.',
    badge: 'Visual',
    badgeColor: '#BF5AF2',
    uses: ['Equipment failures', 'Quality issues', 'Environmental incidents'],
  },
  {
    value: 'bow_tie',
    label: 'Bow Tie',
    full: 'Bow Tie Risk Analysis',
    description: 'Maps threats, top event, consequences, and barriers. Excellent for linking the incident back to the risk register and identifying barrier failures.',
    badge: 'Risk-linked',
    badgeColor: '#FF9500',
    uses: ['Hazard analysis', 'Barrier failures', 'Major incident prevention'],
  },
  {
    value: 'hfacs',
    label: 'HFACS',
    full: 'Human Factors Analysis & Classification',
    description: 'Based on the Swiss Cheese Model. Categorises human and organisational failures across four levels. Used in aviation, mining, and healthcare.',
    badge: 'Human factors',
    badgeColor: '#FF3B5C',
    uses: ['Human error', 'Systems-level failures', 'Repeat incidents'],
  },
]

const SEV_COLOUR: Record<string, string> = {
  critical: '#DC2626', high: '#D97706', medium: '#2563EB', low: '#16A34A',
}

const CAT_LABEL: Record<string, string> = {
  injury: 'Injury', near_miss: 'Near Miss', property_damage: 'Property Damage',
  environmental: 'Environmental', hazard: 'Hazard', illness: 'Illness',
}

export default function InvestigationNew() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [form, setForm] = useState({
    title: '',
    incident_id: searchParams.get('incident_id') || '',
    methodology: 'icam',
    classification: 'standard',
    lead_investigator_id: '',
    target_completion: '',
  })
  const [users, setUsers] = useState<any[]>([])
  const [linkedIncident, setLinkedIncident] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [step, setStep] = useState<1 | 2>(1)

  // Incident picker
  const [incidents, setIncidents] = useState<any[]>([])
  const [incSearch, setIncSearch] = useState('')
  const [pickerOpen, setPickerOpen] = useState(false)
  const [incLoading, setIncLoading] = useState(false)

  useEffect(() => {
    api.get('/users').then(r => setUsers(r.data || [])).catch(() => {})
    const presetId = searchParams.get('incident_id')
    if (presetId) {
      api.get(`/incidents/${presetId}`).then(r => {
        const inc = r.data
        setLinkedIncident(inc)
        setForm(f => ({
          ...f,
          title: f.title || `Investigation — ${(inc.description || '').slice(0, 80)}`,
          classification: inc.ai_severity === 'critical' ? 'notifiable' : inc.ai_severity === 'high' ? 'serious' : f.classification,
        }))
      }).catch(() => {})
    }
  }, [])

  useEffect(() => {
    if (!pickerOpen) return
    setIncLoading(true)
    api.get('/investigations/incidents-search', { params: incSearch ? { q: incSearch } : {} })
      .then(r => setIncidents(r.data || []))
      .catch(() => {})
      .finally(() => setIncLoading(false))
  }, [pickerOpen, incSearch])

  const selectIncident = (inc: any) => {
    setLinkedIncident(inc)
    setPickerOpen(false)
    setIncSearch('')
    setForm(f => ({
      ...f,
      incident_id: inc.id,
      classification: inc.ai_severity === 'critical' ? 'notifiable' : inc.ai_severity === 'high' ? 'serious' : f.classification,
      title: f.title.trim() ? f.title : `Investigation — ${(inc.description || '').slice(0, 80)}`,
    }))
  }

  const clearIncident = () => {
    setLinkedIncident(null)
    setForm(f => ({ ...f, incident_id: '' }))
    setIncSearch('')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.title.trim() || !form.lead_investigator_id) {
      setError('Title and lead investigator are required')
      return
    }
    setLoading(true)
    setError('')
    try {
      const { data } = await api.post('/investigations', {
        ...form,
        incident_id: form.incident_id || undefined,
        target_completion: form.target_completion || undefined,
      })
      navigate(`/investigations/${data.id}`)
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create investigation')
    }
    setLoading(false)
  }

  const selectedMethod = METHODOLOGIES.find(m => m.value === form.methodology)!

  // Step 1 — methodology picker
  if (step === 1) {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>New Investigation</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>Step 1 of 2 — Choose a methodology</p>
        </div>
        <div className="space-y-3">
          {METHODOLOGIES.map(m => {
            const active = form.methodology === m.value
            return (
              <button
                key={m.value}
                type="button"
                onClick={() => setForm(f => ({ ...f, methodology: m.value }))}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  padding: '16px 18px',
                  borderRadius: 12,
                  border: `2px solid ${active ? m.badgeColor : 'var(--border)'}`,
                  background: active ? `${m.badgeColor}08` : 'var(--card)',
                  cursor: 'pointer',
                  transition: 'all 150ms',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                  <span style={{ fontWeight: 800, fontSize: 15, color: 'var(--text)' }}>{m.label}</span>
                  <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', fontWeight: 500 }}>{m.full}</span>
                  <span style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: `${m.badgeColor}20`, color: m.badgeColor }}>
                    {m.badge}
                  </span>
                </div>
                <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 8px', lineHeight: 1.5 }}>{m.description}</p>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {m.uses.map(u => (
                    <span key={u} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 999, background: 'var(--bg)', color: 'var(--text-muted)', border: '1px solid var(--border)' }}>{u}</span>
                  ))}
                </div>
              </button>
            )
          })}
        </div>
        <div className="flex gap-3">
          <button
            type="button"
            className="btn btn-primary"
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            onClick={() => setStep(2)}
          >
            Continue with {selectedMethod.label} <ChevronRight size={16} />
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/investigations')}>Cancel</button>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 2 }}>
          <button type="button" onClick={() => setStep(1)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 13, padding: 0 }}>
            ← Back
          </button>
          <span style={{ fontSize: 12, padding: '2px 10px', borderRadius: 999, background: `${selectedMethod.badgeColor}20`, color: selectedMethod.badgeColor, fontWeight: 700 }}>
            {selectedMethod.label}
          </span>
        </div>
        <h1 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>New Investigation</h1>
        <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>Step 2 of 2 — Investigation details</p>
      </div>

      <form onSubmit={handleSubmit} className="card space-y-5">

        {/* Incident picker */}
        <div>
          <label>
            Linked Incident{' '}
            <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional — prefills title &amp; classification)</span>
          </label>

          {linkedIncident ? (
            <div style={{
              position: 'relative', borderRadius: 10, padding: '12px 40px 12px 14px',
              border: '1.5px solid var(--accent)', background: 'var(--accent-light)',
            }}>
              <button
                type="button"
                onClick={clearIncident}
                style={{ position: 'absolute', top: 8, right: 8, background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
              >
                <X size={14} style={{ color: 'var(--text-muted)' }} />
              </button>

              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                <Link to={`/incidents/${linkedIncident.id}`} target="_blank"
                  style={{ fontWeight: 700, fontSize: 14, color: 'var(--accent)' }}>
                  {linkedIncident.incident_number}
                </Link>
                {linkedIncident.ai_severity && (
                  <span style={{
                    fontSize: 10, padding: '1px 6px', borderRadius: 4, fontWeight: 700,
                    background: SEV_COLOUR[linkedIncident.ai_severity] + '20',
                    color: SEV_COLOUR[linkedIncident.ai_severity],
                  }}>{linkedIncident.ai_severity}</span>
                )}
                {linkedIncident.category && (
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{CAT_LABEL[linkedIncident.category] || linkedIncident.category}</span>
                )}
                <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 'auto' }}>
                  {new Date(linkedIncident.date_occurred).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                </span>
              </div>

              <p style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.5, margin: 0 }}>
                {(linkedIncident.description || '').slice(0, 160)}
              </p>

              {(linkedIncident.site_name || linkedIncident.injury_type) && (
                <div style={{ display: 'flex', gap: 12, marginTop: 5, fontSize: 12, color: 'var(--text-muted)', flexWrap: 'wrap' }}>
                  {linkedIncident.site_name && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                      <MapPin size={11} /> {linkedIncident.site_name}
                    </span>
                  )}
                  {linkedIncident.injury_type && <span>Injury: {linkedIncident.injury_type}</span>}
                  {linkedIncident.activity_at_time && <span>Activity: {linkedIncident.activity_at_time}</span>}
                </div>
              )}
            </div>
          ) : (
            <div style={{ position: 'relative' }}>
              <div style={{ position: 'relative' }}>
                <Search size={14} style={{
                  position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
                  color: 'var(--text-muted)', pointerEvents: 'none',
                }} />
                <input
                  style={{ paddingLeft: 32 }}
                  placeholder="Search by incident number or description…"
                  value={incSearch}
                  onFocus={() => setPickerOpen(true)}
                  onChange={e => { setIncSearch(e.target.value); setPickerOpen(true) }}
                  onBlur={() => setTimeout(() => setPickerOpen(false), 160)}
                />
              </div>

              {pickerOpen && (
                <div style={{
                  position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 50,
                  background: '#fff', border: '1px solid var(--border)', borderRadius: 10,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)', maxHeight: 300, overflowY: 'auto',
                }}>
                  {incLoading ? (
                    <div style={{ padding: '12px 16px', fontSize: 13, color: 'var(--text-muted)' }}>Loading…</div>
                  ) : incidents.length === 0 ? (
                    <div style={{ padding: '12px 16px', fontSize: 13, color: 'var(--text-muted)' }}>No incidents found</div>
                  ) : incidents.map((inc, i) => (
                    <div
                      key={inc.id}
                      onMouseDown={() => selectIncident(inc)}
                      style={{
                        padding: '10px 14px', cursor: 'pointer',
                        borderBottom: i < incidents.length - 1 ? '1px solid var(--border)' : 'none',
                        background: 'transparent',
                        transition: 'background 0.1s',
                      }}
                      onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg)')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 3 }}>
                        <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--accent)' }}>{inc.incident_number}</span>
                        {inc.ai_severity && (
                          <span style={{
                            fontSize: 10, padding: '1px 5px', borderRadius: 4, fontWeight: 600,
                            background: SEV_COLOUR[inc.ai_severity] + '20', color: SEV_COLOUR[inc.ai_severity],
                          }}>{inc.ai_severity}</span>
                        )}
                        {inc.category && (
                          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{CAT_LABEL[inc.category] || inc.category}</span>
                        )}
                        <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 'auto' }}>
                          {new Date(inc.date_occurred).toLocaleDateString('en-AU')}
                        </span>
                      </div>
                      <p style={{ fontSize: 12, color: 'var(--text)', margin: 0, lineHeight: 1.4 }}>
                        {(inc.description || '').slice(0, 110)}
                      </p>
                      {inc.site_name && (
                        <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '2px 0 0', display: 'flex', alignItems: 'center', gap: 3 }}>
                          <MapPin size={10} /> {inc.site_name}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Title */}
        <div>
          <label>Investigation Title *</label>
          <input
            value={form.title}
            onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
            placeholder="Brief descriptive title for this investigation"
            required
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label>Classification</label>
            <select value={form.classification} onChange={e => setForm(f => ({ ...f, classification: e.target.value }))}>
              <option value="standard">Standard</option>
              <option value="serious">Serious</option>
              <option value="notifiable">Notifiable</option>
            </select>
          </div>
          <div style={{ gridColumn: '1 / -1', display: 'none' }}>{/* methodology stored in state */}</div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label>Lead Investigator *</label>
            <select value={form.lead_investigator_id} onChange={e => setForm(f => ({ ...f, lead_investigator_id: e.target.value }))} required>
              <option value="">— Select lead —</option>
              {users.map(u => (
                <option key={u.id} value={u.id}>{u.first_name} {u.last_name} ({u.role?.replace('_', ' ')})</option>
              ))}
            </select>
          </div>
          <div>
            <label>Target Completion</label>
            <input type="date" value={form.target_completion} onChange={e => setForm(f => ({ ...f, target_completion: e.target.value }))} />
          </div>
        </div>

        {error && (
          <div className="flex items-start gap-2 text-sm p-3 rounded-lg" style={{ background: 'var(--red-light)', color: 'var(--red)' }}>
            <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />{error}
          </div>
        )}

        <div className="flex gap-3">
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Creating...' : 'Open Investigation'}
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/investigations')}>Cancel</button>
        </div>
      </form>
    </div>
  )
}
